'use client'

import { CloseOutlined, RefreshOutlined, SearchOutlined, WarningAmberOutlined } from '@mui/icons-material'
import {
  Box,
  Button,
  Chip,
  Drawer,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography
} from '@mui/material'
import { useStatsigClient } from '@statsig/react-bindings'
import { usePathname } from 'next/navigation'
import { useEffect, useMemo, useState, useSyncExternalStore } from 'react'

import type { StatsigGateId } from '~/configs/statsig'
import { useBannerOffset } from '~/store/slices/display'

import { ALL_GATE_IDS, GATE_LABELS, SERVER_EVALUATED_GATES } from './gateLabels'
import {
  getScreenGates,
  subscribeToScreenGates,
  syncScreenPath,
  UNOVERRIDABLE_GATES,
  withOverridesBypassed
} from './gateOverrides'
import useGateOverrides from './useGateOverrides'

const DRAWER_WIDTH = 460

/**
 * Synop's Statsig project. Not a credential — it is the path segment of every
 * console URL and the console authenticates independently — and the client SDK
 * key beside it in `~/configs/env` is already hard-coded, so an env var would
 * add deploy plumbing for no security gain.
 */
const STATSIG_PROJECT_ID = '1ocIMbk4oS9GA871JfmF28'

/** Gate IDs double as the console's path segment, so no lookup is needed. */
function gateConsoleUrl(gateId: StatsigGateId): string {
  return `https://console.statsig.com/${STATSIG_PROJECT_ID}/gates/${gateId}`
}

/** Which slice of the registry the list is showing. */
type Scope = 'screen' | 'overridden' | 'all'

const EMPTY_SCOPE_MESSAGE: Record<Scope, string> = {
  screen: 'This screen did not read any feature gates.',
  overridden: 'No gates are overridden.',
  all: 'No gates match.'
}

type GateRowProps = {
  gateId: StatsigGateId
  override: boolean | undefined
  onChange: (gateId: StatsigGateId, next: boolean, evaluated: boolean) => void
}

function GateRow({ gateId, override, onChange }: GateRowProps) {
  const { checkGate } = useStatsigClient()
  const locked = UNOVERRIDABLE_GATES.has(gateId)
  const serverEvaluated = SERVER_EVALUATED_GATES.has(gateId)
  // Exposures are suppressed: rendering a list of every gate would otherwise
  // log an exposure per row per render and skew the metrics of any gate
  // currently running as an experiment.
  const evaluatedValue = withOverridesBypassed(() => checkGate(gateId, { disableExposureLog: true }))
  // An override equal to the evaluated value is indistinguishable from no
  // override at all — nothing reads the evaluation reason — so the row reports
  // only genuine deviations.
  const isOverridden = override !== undefined && override !== evaluatedValue
  const checked = override ?? evaluatedValue

  return (
    <Box
      sx={{
        px: 2,
        py: 1.5,
        borderLeft: 3,
        borderLeftColor: isOverridden ? 'warning.main' : 'transparent',
        bgcolor: isOverridden ? 'action.hover' : 'transparent'
      }}
    >
      <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Link
              href={gateConsoleUrl(gateId)}
              target="_blank"
              rel="noopener noreferrer"
              variant="body2"
              aria-label={`Open ${gateId} in the Statsig console (new tab)`}
              sx={{ fontFamily: 'monospace', fontWeight: 600, wordBreak: 'break-all' }}
            >
              {gateId}
            </Link>
            {serverEvaluated && (
              <Tooltip title="A backend service evaluates this gate too. Overriding it only changes what this browser renders.">
                <WarningAmberOutlined sx={{ fontSize: 15, color: 'warning.main', flexShrink: 0 }} />
              </Tooltip>
            )}
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {GATE_LABELS[gateId]}
          </Typography>
          <Typography variant="caption" color="text.disabled">
            Statsig: {evaluatedValue ? 'ON' : 'OFF'}
          </Typography>
        </Box>

        {locked ? (
          <Tooltip title="This gate decides who can open this panel, so it cannot be overridden here.">
            <Chip size="small" label="Locked" sx={{ flexShrink: 0 }} />
          </Tooltip>
        ) : (
          <Switch
            size="small"
            checked={checked}
            onChange={(event) => onChange(gateId, event.target.checked, evaluatedValue)}
            inputProps={{ 'aria-label': `Override ${gateId}` }}
            sx={{ flexShrink: 0 }}
          />
        )}
      </Stack>
    </Box>
  )
}

export type FeatureFlagsPanelProps = {
  open: boolean
  onClose: () => void
}

/**
 * Synop-internal panel for forcing Statsig gates on or off at runtime.
 *
 * Deliberately a persistent (non-modal) drawer: the point of the panel is to
 * watch the page change as a gate flips, which a modal backdrop would cover.
 * It renders no backdrop, traps no focus and locks no scroll.
 */
export default function FeatureFlagsPanel({ open, onClose }: FeatureFlagsPanelProps) {
  const [query, setQuery] = useState('')
  // Opens scoped to the current screen, which is the question the panel exists
  // to answer ("why is this page not showing me X?"). Falls back to the full
  // registry when the screen read nothing, so the panel never opens on an
  // empty list.
  const [scope, setScope] = useState<Scope>(() => (getScreenGates().size > 0 ? 'screen' : 'all'))
  const { overrides, overriddenCount, setOverride, resetAll } = useGateOverrides()
  const bannerOffset = useBannerOffset()
  const pathname = usePathname()

  // Subscribed rather than read once: gates are recorded as components render,
  // and plenty mount after the first pass — tab bodies on selection, sections
  // once their data lands. Without this the list would silently miss them.
  const screenGates = useSyncExternalStore(subscribeToScreenGates, getScreenGates, getScreenGates)

  // Covers a route that reads no gates at all, which the adapter's own lazy
  // reset cannot detect. Safe in an effect precisely because that lazy reset
  // already handles routes that do read gates, so by the time this runs the
  // path either matches and it no-ops, or nothing was recorded and it clears.
  useEffect(() => {
    syncScreenPath(pathname)
  }, [pathname])

  // Reopening returns to the screen scope: the panel outlives any single open,
  // so without this it reopens on whatever slice was last browsed rather than
  // on the question it exists to answer.
  useEffect(() => {
    if (!open) return
    setScope(getScreenGates().size > 0 ? 'screen' : 'all')
    setQuery('')
  }, [open])

  const visibleGates = useMemo(() => {
    const normalized = query.trim().toLowerCase()
    return ALL_GATE_IDS.filter((gateId) => {
      if (scope === 'overridden' && overrides[gateId] === undefined) return false
      if (scope === 'screen' && !screenGates.has(gateId)) return false
      if (!normalized) return true
      return gateId.toLowerCase().includes(normalized) || GATE_LABELS[gateId].toLowerCase().includes(normalized)
    })
  }, [query, scope, overrides, screenGates])

  // Returning the switch to what Statsig resolved drops the override rather
  // than pinning that value, so the two positions cover all three states: the
  // switch differs from the baseline, or there is no override.
  const handleChange = (gateId: StatsigGateId, next: boolean, evaluated: boolean) => {
    setOverride(gateId, next === evaluated ? null : next)
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      variant="persistent"
      aria-label="Feature flags"
      PaperProps={{
        sx: {
          position: 'fixed',
          width: { xs: '100%', sm: DRAWER_WIDTH },
          top: bannerOffset,
          height: `calc(100% - ${bannerOffset}px)`
        }
      }}
    >
      <Stack sx={{ height: '100%' }}>
        <Stack spacing={1.5} sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Feature flags
            </Typography>
            <IconButton size="small" onClick={onClose} aria-label="Close feature flags">
              <CloseOutlined sx={{ fontSize: 18 }} />
            </IconButton>
          </Stack>

          <Typography variant="caption" color="text.secondary">
            Overrides apply to this browser only and are cleared when the browser closes. Backend services are
            unaffected.
          </Typography>

          <TextField
            size="small"
            fullWidth
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search gates"
            slotProps={{
              htmlInput: { 'aria-label': 'Search gates' },
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchOutlined sx={{ fontSize: 18 }} />
                  </InputAdornment>
                )
              }
            }}
          />

          <ToggleButtonGroup
            exclusive
            size="small"
            value={scope}
            onChange={(_event, next: Scope | null) => next && setScope(next)}
            aria-label="Which gates to list"
            fullWidth
          >
            <ToggleButton value="screen" sx={{ textTransform: 'none' }}>
              This screen ({screenGates.size})
            </ToggleButton>
            <ToggleButton value="overridden" sx={{ textTransform: 'none' }}>
              Overridden ({overriddenCount})
            </ToggleButton>
            <ToggleButton value="all" sx={{ textTransform: 'none' }}>
              All ({ALL_GATE_IDS.length})
            </ToggleButton>
          </ToggleButtonGroup>

          <Stack direction="row" spacing={1} alignItems="center">
            <Box sx={{ flexGrow: 1 }} />
            <Button size="small" onClick={resetAll} disabled={overriddenCount === 0}>
              Reset all
            </Button>
            <Tooltip title="Some gates are only read when a view first mounts. Reload to apply those.">
              <Button size="small" startIcon={<RefreshOutlined />} onClick={() => window.location.reload()}>
                Reload
              </Button>
            </Tooltip>
          </Stack>
        </Stack>

        <Box sx={{ overflowY: 'auto', flexGrow: 1 }}>
          {visibleGates.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
              {query.trim() ? 'No gates match.' : EMPTY_SCOPE_MESSAGE[scope]}
            </Typography>
          ) : (
            visibleGates.map((gateId) => (
              <GateRow
                key={gateId}
                gateId={gateId}
                override={overrides[gateId]}
                onChange={handleChange}
              />
            ))
          )}
        </Box>
      </Stack>
    </Drawer>
  )
}
